import React, { useState, useEffect, useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import api, { monthlyContactAPI, staffAPI, clientAPI, lookupAPI } from '../services/api';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import {
  groupMonthlyContactEmailSelectOptions,
  monthlyGroupHasSpanishTemplate,
  resolveLetterTemplateLangCodeFromClientRow,
} from '../utils/clientLetterTemplateGroups';
import ContentHeader from '../components/ContentHeader';
import InfoIconButton from '../components/InfoIconButton';
import { formatDisplayDate } from '../utils/dateFormat';
import './MonthlyContact.css';

const CONTACT_METHOD_LABELS = {
  meeting: 'Meeting',
  phone: 'Phone',
  email: 'Email (legacy)',
  email_personal: 'Email (personal)',
  email_system: 'Email (system)',
  text: 'Text',
  letter: 'Letter',
};

/** View mode: 'needing' = list needing contact; otherwise show clients contacted by that method */
const SUMMARY_VIEW_NEEDING = 'needing';
const SUMMARY_METHOD_KEYS = ['meeting', 'phone', 'email_personal', 'email_system', 'text', 'letter'];

function formatDate(d) {
  return formatDisplayDate(d);
}

function MonthlyContact() {
  const { tenantPath } = useTenantFromUrl();
  const [featureEnabled, setFeatureEnabled] = useState(null);
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [staffFilter, setStaffFilter] = useState('');
  const [roleCode, setRoleCode] = useState(null);
  const [myStaffId, setMyStaffId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  /** Normalized message key (shared EN/ES display); send/preview resolve template per client language. */
  const [selectedGroupKey, setSelectedGroupKey] = useState('');
  const [languages, setLanguages] = useState([]);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [contactSummaryHintOpen, setContactSummaryHintOpen] = useState(false);
  const [generateEmailHintOpen, setGenerateEmailHintOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [sendAllResult, setSendAllResult] = useState(null);
  const [recordingContact, setRecordingContact] = useState(false);
  const [recordContactClientIds, setRecordContactClientIds] = useState(null);
  const [clientSort, setClientSort] = useState('asc'); // 'asc' | 'desc' — Client column
  const [savingClientId, setSavingClientId] = useState(null); // id of client being saved (inline edit)
  /** Which summary card is selected: 'needing' (default) or a contact method key */
  const [summaryViewMode, setSummaryViewMode] = useState(SUMMARY_VIEW_NEEDING);

  const setSummaryViewModeAndClearSendResult = (mode) => {
    setSummaryViewMode(mode);
    if (mode !== SUMMARY_VIEW_NEEDING) {
      setSendAllResult(null);
      setRecordContactClientIds(null);
    }
  };

  useEffect(() => {
    if (!previewOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setPreviewOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewOpen]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/whoami');
        setFeatureEnabled(res.data?.features?.monthly_contact === true);
        setRoleCode(res.data?.membership?.role_code ?? null);
        const sid = res.data?.user?.staff_id ?? null;
        setMyStaffId(sid != null ? String(sid) : null);
      } catch {
        setFeatureEnabled(false);
        setRoleCode(null);
        setMyStaffId(null);
      }
    })();
  }, []);

  useEffect(() => {
    if (roleCode === 'SERVICE_COORDINATOR') return;
    staffAPI.getAll({ active: 'true' }).then((res) => {
      setStaffList(res.data?.data ?? []);
    }).catch(() => setStaffList([]));
  }, [roleCode]);

  useEffect(() => {
    lookupAPI
      .getLanguages()
      .then((res) => setLanguages(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setLanguages([]));
  }, []);

  // Load email templates for Monthly Contact (endpoint uses work_tasks.view so View-as-SC sees templates)
  useEffect(() => {
    setTemplatesLoading(true);
    monthlyContactAPI
      .getEmailTemplates()
      .then((res) => {
        const monthlyContact = res.data?.data ?? [];
        setEmailTemplates(Array.isArray(monthlyContact) ? monthlyContact : []);
      })
      .catch(() => setEmailTemplates([]))
      .finally(() => setTemplatesLoading(false));
  }, []);

  const templateGroups = useMemo(() => groupMonthlyContactEmailSelectOptions(emailTemplates), [emailTemplates]);

  useEffect(() => {
    if (!templateGroups.length) {
      setSelectedGroupKey('');
      return;
    }
    setSelectedGroupKey((prev) => {
      if (prev && templateGroups.some((g) => g.groupKey === prev)) return prev;
      return templateGroups[0].groupKey;
    });
  }, [templateGroups]);

  const selectedOptionGroup = useMemo(
    () => templateGroups.find((g) => g.groupKey === selectedGroupKey),
    [templateGroups, selectedGroupKey]
  );

  const spanishNoEsFallbackCount = useMemo(() => {
    if (!selectedOptionGroup || !languages.length) return 0;
    if (monthlyGroupHasSpanishTemplate(selectedOptionGroup)) return 0;
    return clients.filter((c) => resolveLetterTemplateLangCodeFromClientRow(c, languages) === 'ES').length;
  }, [selectedOptionGroup, clients, languages]);

  useEffect(() => {
    let cancelled = false;
    // Managers must have a specific SC selected; never request "all" org-wide.
    const needStaffSelection = roleCode && roleCode !== 'SERVICE_COORDINATOR';
    if (needStaffSelection && !staffFilter) {
      setLoading(false);
      setClients([]);
      setTotal(0);
      setCounts(null);
      return;
    }
    setLoading(true);
    const params = {};
    if (roleCode === 'SERVICE_COORDINATOR' && myStaffId) {
      params.staffId = myStaffId;
    } else if (staffFilter) {
      params.staffId = staffFilter;
    }
    const listPromise =
      summaryViewMode === SUMMARY_VIEW_NEEDING
        ? monthlyContactAPI.getNeeded(params)
        : monthlyContactAPI.getByMethod({ ...params, method: summaryViewMode });
    Promise.all([listPromise, monthlyContactAPI.getCounts(params)])
      .then(([listRes, countsRes]) => {
        if (cancelled) return;
        setClients(listRes.data?.data ?? []);
        setTotal(listRes.data?.total ?? 0);
        setCounts(countsRes.data?.data ?? null);
      })
      .catch((err) => {
        if (!cancelled) {
          setClients([]);
          setTotal(0);
          setCounts(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [roleCode, myStaffId, staffFilter, summaryViewMode]);

  const handlePreview = async () => {
    if (!selectedGroupKey || clients.length === 0) return;
    const first = clients[0];
    setPreviewLoading(true);
    setPreviewOpen(true);
    setPreviewData(null);
    setGenerateError(null);
    try {
      const res = await monthlyContactAPI.preview({ templateGroupKey: selectedGroupKey, clientId: first.id });
      setPreviewData(res.data?.data ?? null);
    } catch (err) {
      setPreviewData(null);
      setGenerateError(err.response?.data?.error || err.message || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendAll = async () => {
    if (!selectedGroupKey || clients.length === 0) return;
    if (!window.confirm(`Send ${clients.length} email(s) from your Microsoft 365 mailbox? Each client will receive one email.`)) return;
    if (spanishNoEsFallbackCount > 0) {
      window.alert(
        `This template is only available in English. ${spanishNoEsFallbackCount} client(s) whose preferred language is Spanish will receive the English version.`
      );
    }
    setGenerateError(null);
    setSendAllResult(null);
    setRecordContactClientIds(null);
    setSendingAll(true);
    try {
      const res = await monthlyContactAPI.sendAll({
        templateGroupKey: selectedGroupKey,
        clientIds: clients.map((c) => c.id),
      });
      const data = res.data?.data ?? {};
      setSendAllResult(data);
      if (data.sentList && data.sentList.length > 0) {
        setRecordContactClientIds(data.sentList.map((s) => s.clientId));
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Send failed';
      const code = err.response?.data?.code;
      setGenerateError(code === 'NO_MICROSOFT_TOKEN' || code === 'TOKEN_EXPIRED'
        ? `${msg}`
        : msg);
      setSendAllResult(null);
    } finally {
      setSendingAll(false);
    }
  };

  const refetchListAndCounts = () => {
    const params = {};
    if (roleCode === 'SERVICE_COORDINATOR' && myStaffId) params.staffId = myStaffId;
    else if (staffFilter) params.staffId = staffFilter;
    const listPromise =
      summaryViewMode === SUMMARY_VIEW_NEEDING
        ? monthlyContactAPI.getNeeded(params)
        : monthlyContactAPI.getByMethod({ ...params, method: summaryViewMode });
    listPromise.then((res) => {
      setClients(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
    }).catch(() => {});
    monthlyContactAPI.getCounts(params).then((res) => {
      setCounts(res.data?.data ?? null);
    }).catch(() => {});
  };

  const handleRecordContact = async () => {
    if (!recordContactClientIds || recordContactClientIds.length === 0) return;
    setRecordingContact(true);
    setGenerateError(null);
    try {
      await monthlyContactAPI.recordContact({ clientIds: recordContactClientIds });
      setRecordContactClientIds(null);
      setSendAllResult(null);
      refetchListAndCounts();
    } catch (err) {
      setGenerateError(err.response?.data?.error || err.message || 'Update failed');
    } finally {
      setRecordingContact(false);
    }
  };

  const sortedClients = useMemo(() => {
    const dir = clientSort === 'asc' ? 1 : -1;
    return [...clients].sort((a, b) => {
      const aName = [a.last_name || '', a.first_name || ''].join(' ').trim().toLowerCase();
      const bName = [b.last_name || '', b.first_name || ''].join(' ').trim().toLowerCase();
      return dir * aName.localeCompare(bName, undefined, { sensitivity: 'base' });
    });
  }, [clients, clientSort]);

  const handleUpdateLastContact = async (clientId, last_contacted_at, last_contact_method) => {
    setSavingClientId(clientId);
    setGenerateError(null);
    try {
      const methodStored =
        last_contact_method === 'in_person' ? 'meeting' : last_contact_method || null;
      await clientAPI.update(clientId, {
        last_contacted_at: last_contacted_at || null,
        last_contact_method: methodStored,
      });
      setClients((prev) =>
        prev.map((c) =>
          c.id === clientId
            ? { ...c, last_contacted_at: last_contacted_at || null, last_contact_method: methodStored }
            : c
        )
      );
    } catch (err) {
      setGenerateError(err.response?.data?.error || err.message || 'Failed to update');
    } finally {
      setSavingClientId(null);
    }
  };

  const handleDownloadAll = async () => {
    if (!selectedGroupKey || clients.length === 0) return;
    if (!window.confirm(`Download email text for all ${clients.length} client(s) as .txt files?`)) return;
    setGenerateError(null);
    setDownloadingAll(true);
    try {
      for (let i = 0; i < clients.length; i++) {
        const c = clients[i];
        const res = await monthlyContactAPI.preview({ templateGroupKey: selectedGroupKey, clientId: c.id });
        const data = res.data?.data ?? {};
        const name = [c.last_name, c.first_name].filter(Boolean).join('_') || `client_${c.id}`;
        const text = `Subject: ${data.subject || ''}\n\n${data.body || ''}`;
        const blob = new Blob([text], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `email_${name}.txt`.replace(/\s+/g, '_');
        a.click();
        window.URL.revokeObjectURL(url);
        if (i < clients.length - 1) await new Promise((r) => setTimeout(r, 200));
      }
    } catch (err) {
      setGenerateError(err.response?.data?.error || err.message || 'Download failed');
    } finally {
      setDownloadingAll(false);
    }
  };

  const canFilterByStaff = roleCode && roleCode !== 'SERVICE_COORDINATOR';
  const isServiceCoordinator = roleCode === 'SERVICE_COORDINATOR';

  // Managers must pick one SC; no "All" (no org-wide blast). Default to first SC when list loads.
  useEffect(() => {
    if (canFilterByStaff && staffList.length > 0 && !staffFilter) {
      setStaffFilter(String(staffList[0].id));
    }
  }, [canFilterByStaff, staffList, staffFilter]);

  if (featureEnabled === false) {
    return <Navigate to={tenantPath('/dashboard')} replace />;
  }
  if (featureEnabled === null) {
    return <div className="monthly-contact-page"><p>Loading…</p></div>;
  }

  return (
    <div className="monthly-contact-page">
      <ContentHeader
        title="Monthly Contact"
        titleHint="This list shows non-intake clients who have not been contacted this month. Intake clients (no initial IFSP date yet) are excluded from monthly contact."
      />
      {isServiceCoordinator && (
        <p className="monthly-contact-sc-note">
          You see only your own caseload. Monthly contact email is sent only for clients assigned to you.
        </p>
      )}

      {counts != null && (
        <section className="monthly-contact-counts">
          <h3 className="monthly-contact-section-title-with-hint">
            Contact summary
            <InfoIconButton
              ariaExpanded={contactSummaryHintOpen}
              onClick={() => setContactSummaryHintOpen((prev) => !prev)}
              ariaLabel="Show contact summary explanation"
              ariaLabelExpanded="Hide contact summary explanation"
            />
          </h3>
          {contactSummaryHintOpen && (
            <p className="monthly-contact-hint-text">
              The first box is how many non-intake clients need contact this month. The other boxes show how many were contacted by each method. Click a card to see that list below.
            </p>
          )}
          <div className="monthly-contact-count-cards">
            <button
              type="button"
              className={`monthly-contact-card monthly-contact-card-need monthly-contact-card-btn ${summaryViewMode === SUMMARY_VIEW_NEEDING ? 'monthly-contact-card-selected' : ''}`}
              title="Clients due for contact this month"
              onClick={() => setSummaryViewModeAndClearSendResult(SUMMARY_VIEW_NEEDING)}
            >
              <span className="monthly-contact-card-value">{counts.needingContact}</span>
              <span className="monthly-contact-card-label">Need contact</span>
            </button>
            {SUMMARY_METHOD_KEYS.map((methodKey) => (
              <button
                key={methodKey}
                type="button"
                className={`monthly-contact-card monthly-contact-card-method monthly-contact-card-btn ${summaryViewMode === methodKey ? 'monthly-contact-card-selected' : ''}`}
                title={`Clients contacted ${(CONTACT_METHOD_LABELS[methodKey] || methodKey).toLowerCase()}`}
                onClick={() => setSummaryViewModeAndClearSendResult(methodKey)}
              >
                <span className="monthly-contact-card-value">
                  {counts[methodKey] ?? 0}
                </span>
                <span className="monthly-contact-card-label">{CONTACT_METHOD_LABELS[methodKey]}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="monthly-contact-actions">
        {canFilterByStaff && (
          <div className="monthly-contact-filter">
            <label htmlFor="staff-filter">View clients for service coordinator:</label>
            <select
              id="staff-filter"
              value={staffFilter}
              onChange={(e) => setStaffFilter(e.target.value)}
            >
              {staffList.map((s) => (
                <option key={s.id} value={s.id}>
                  {[s.last_name, s.first_name].filter(Boolean).join(', ') || s.id}
                </option>
              ))}
            </select>
            <span className="monthly-contact-filter-hint">One coordinator at a time; no org-wide list.</span>
          </div>
        )}
        <div className="monthly-contact-docgen">
          <label htmlFor="monthly-contact-template" className="monthly-contact-label-with-hint">
            Generate email
            <InfoIconButton
              ariaExpanded={generateEmailHintOpen}
              onClick={() => setGenerateEmailHintOpen((prev) => !prev)}
              ariaLabel="Show generate email explanation"
              ariaLabelExpanded="Hide generate email explanation"
            />
            :
          </label>
          {generateEmailHintOpen && (
            <p className="monthly-contact-hint-text monthly-contact-docgen-hint-text">
              Choose a monthly email message (Monthly Contact category from Settings → Email Templates). Each message may exist in English and/or Spanish; the list shows one entry per message. Send, preview, and download pick English or Spanish from each client’s language (same order as Client Email: client and primary contact language). If only English exists for a message, Spanish-preference clients receive English—you’ll be reminded before send.
            </p>
          )}
          <div className="monthly-contact-docgen-picker-row">
            <select
              id="monthly-contact-template"
              value={selectedGroupKey}
              onChange={(e) => setSelectedGroupKey(e.target.value)}
              disabled={templatesLoading}
              className="monthly-contact-template-select"
            >
              {templatesLoading && <option value="">Loading email templates…</option>}
              {!templatesLoading && templateGroups.length === 0 && <option value="">No email templates (Monthly Contact)</option>}
              {templateGroups.map((g) => (
                <option key={g.groupKey} value={g.groupKey}>
                  {g.displayName}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn monthly-contact-preview-btn"
              onClick={handlePreview}
              disabled={summaryViewMode !== SUMMARY_VIEW_NEEDING || templatesLoading || previewLoading || !selectedGroupKey || clients.length === 0}
              title={summaryViewMode !== SUMMARY_VIEW_NEEDING ? 'Available only for "Need contact" list' : undefined}
            >
              {previewLoading ? 'Loading…' : 'Preview'}
            </button>
            <button
              type="button"
              className="btn btn-primary monthly-contact-send-all"
              onClick={handleSendAll}
              disabled={summaryViewMode !== SUMMARY_VIEW_NEEDING || templatesLoading || sendingAll || !selectedGroupKey || clients.length === 0}
              title={summaryViewMode !== SUMMARY_VIEW_NEEDING ? 'Available only for "Need contact" list' : undefined}
            >
              {sendingAll ? 'Sending…' : 'Send all'}
            </button>
            <button
              type="button"
              className="btn monthly-contact-download-all"
              onClick={handleDownloadAll}
              disabled={summaryViewMode !== SUMMARY_VIEW_NEEDING || templatesLoading || downloadingAll || !selectedGroupKey || clients.length === 0}
              title={summaryViewMode !== SUMMARY_VIEW_NEEDING ? 'Available only for "Need contact" list' : undefined}
            >
              {downloadingAll ? 'Downloading…' : 'Download all'}
            </button>
          </div>
        </div>
        {sendAllResult != null && (sendAllResult.sent > 0 || sendAllResult.failed > 0) && (
          <div className="monthly-contact-send-result" role="status">
            <p>
              Sent {sendAllResult.sent} email(s).
              {sendAllResult.failed > 0 && ` ${sendAllResult.failed} failed.`}
            </p>
            {recordContactClientIds && recordContactClientIds.length > 0 && (
              <div className="monthly-contact-record-contact-prompt">
                <p>Update &quot;Last contacted&quot; for these {recordContactClientIds.length} client(s)?</p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleRecordContact}
                  disabled={recordingContact}
                >
                  {recordingContact ? 'Updating…' : 'Yes, update last contact'}
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => { setRecordContactClientIds(null); setSendAllResult(null); }}
                >
                  No
                </button>
              </div>
            )}
          </div>
        )}
        {generateError && <div className="monthly-contact-error" role="alert">{generateError}</div>}
      </section>

      {loading ? (
        <p>Loading…</p>
      ) : (
        <section className="monthly-contact-list">
          <h3>
            {summaryViewMode === SUMMARY_VIEW_NEEDING
              ? `Clients needing contact (${total})`
              : `Clients contacted ${(CONTACT_METHOD_LABELS[summaryViewMode] || summaryViewMode).toLowerCase()} (${total})`}
          </h3>
          {clients.length === 0 ? (
            <p className="monthly-contact-empty">
              {summaryViewMode === SUMMARY_VIEW_NEEDING
                ? 'No clients need monthly contact.'
                : `No clients in this category.`}
            </p>
          ) : (
            <table className="monthly-contact-table">
              <thead>
                <tr>
                  <th
                    className="monthly-contact-sortable"
                    onClick={() => setClientSort((s) => (s === 'asc' ? 'desc' : 'asc'))}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setClientSort((s) => (s === 'asc' ? 'desc' : 'asc')); } }}
                    role="button"
                    tabIndex={0}
                    title="Sort by client name"
                  >
                    Client{clientSort === 'asc' ? ' ↑' : ' ↓'}
                  </th>
                  <th>Assigned to</th>
                  <th title="From the client record. Edit here or when you record a contact.">Last contacted</th>
                  <th>Method</th>
                </tr>
              </thead>
              <tbody>
                {sortedClients.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Link
                        to={tenantPath(`/client/${c.id}`)}
                        className="monthly-contact-client-link"
                      >
                        {[c.last_name, c.first_name].filter(Boolean).join(', ') || '—'}
                      </Link>
                    </td>
                    <td>{[c.assigned_staff_last_name, c.assigned_staff_first_name].filter(Boolean).join(', ') || '—'}</td>
                    <td>
                      {savingClientId === c.id ? (
                        <span className="monthly-contact-cell-saving">Saving…</span>
                      ) : (
                        <input
                          type="date"
                          className="monthly-contact-input-date"
                          value={c.last_contacted_at ? c.last_contacted_at.split('T')[0] : ''}
                          onChange={(e) => {
                            const val = e.target.value || null;
                            setClients((prev) =>
                              prev.map((x) => (x.id === c.id ? { ...x, last_contacted_at: val } : x))
                            );
                          }}
                          onFocus={(e) => {
                            // Baseline at focus; onChange updates state before blur, so comparing to c on blur always matched — never saved date-only edits.
                            e.target.dataset.mcDateAtFocus = e.target.value || '';
                          }}
                          onBlur={(e) => {
                            const val = e.target.value || '';
                            const atFocus = e.target.dataset.mcDateAtFocus ?? '';
                            if (val !== atFocus) {
                              handleUpdateLastContact(c.id, val || null, c.last_contact_method);
                            }
                          }}
                          aria-label={`Last contacted for ${[c.last_name, c.first_name].filter(Boolean).join(' ')}`}
                        />
                      )}
                    </td>
                    <td>
                      {savingClientId === c.id ? (
                        <span className="monthly-contact-cell-saving">Saving…</span>
                      ) : (
                        <select
                          className="monthly-contact-select-method"
                          value={c.last_contact_method || ''}
                          onChange={(e) => {
                            const val = e.target.value || null;
                            handleUpdateLastContact(c.id, c.last_contacted_at, val);
                            setClients((prev) =>
                              prev.map((x) => (x.id === c.id ? { ...x, last_contact_method: val } : x))
                            );
                          }}
                          aria-label={`Contact method for ${[c.last_name, c.first_name].filter(Boolean).join(' ')}`}
                        >
                          <option value="">—</option>
                          {Object.entries(CONTACT_METHOD_LABELS).map(([k, label]) => (
                            <option key={k} value={k}>{label}</option>
                          ))}
                        </select>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {previewOpen && (
        <div
          className="monthly-contact-preview-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="monthly-contact-preview-title"
        >
          <div
            className="monthly-contact-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="monthly-contact-preview-header">
              <h3 id="monthly-contact-preview-title">Preview: first email</h3>
              <button type="button" className="monthly-contact-preview-close" onClick={() => setPreviewOpen(false)} aria-label="Close">×</button>
            </div>
            {previewLoading && <p>Loading preview…</p>}
            {!previewLoading && previewData && (
              <div className="monthly-contact-preview-body">
                <p><strong>To:</strong> {previewData.toEmail || '—'} {previewData.clientName && `(${previewData.clientName})`}</p>
                <p><strong>Subject:</strong> {previewData.subject || '—'}</p>
                <div className="monthly-contact-preview-text">
                  <strong>Body:</strong>
                  <pre>{previewData.body || '—'}</pre>
                </div>
                <button
                  type="button"
                  className="btn monthly-contact-preview-download"
                  onClick={() => {
                    const text = `Subject: ${previewData.subject || ''}\n\n${previewData.body || ''}`;
                    const blob = new Blob([text], { type: 'text/plain' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `email_${(previewData.clientName || 'preview').replace(/\s+/g, '_')}.txt`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                  }}
                >
                  Download email
                </button>
              </div>
            )}
            {!previewLoading && !previewData && !generateError && <p>No preview data.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default MonthlyContact;
